import { useCallback, useEffect, useState } from 'react';
import {
  DriverRouteProgressResponse,
  DriverRouteRow,
  DriverRouteSnapshot,
  fetchDriverRouteProgressSnapshot,
} from '../../../components/api/driver';

type UseDriverRouteProgressResult = {
  snapshot: DriverRouteSnapshot;
  rows: DriverRouteRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const emptySnapshot = (): DriverRouteSnapshot => ({
  driversInRoute: 0,
  driversScheduled: 0,
  routesInProgress: 0,
  routesFinished: 0,
  clientsVisited: 0,
  clientsTotal: 0,
  vmsServiced: 0,
  vmsTotal: 0,
});

function safeNum(v: number | null | undefined): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalizePayload(payload: DriverRouteProgressResponse | null | undefined): {
  snapshot: DriverRouteSnapshot;
  rows: DriverRouteRow[];
} {
  if (!payload) return { snapshot: emptySnapshot(), rows: [] };

  const s = payload.snapshot || emptySnapshot();
  const routes = payload.routes || [];

  return {
    snapshot: {
      driversInRoute: safeNum(s.driversInRoute),
      driversScheduled: safeNum(s.driversScheduled),
      routesInProgress: safeNum(s.routesInProgress),
      routesFinished: safeNum(s.routesFinished),
      clientsVisited: safeNum(s.clientsVisited),
      clientsTotal: safeNum(s.clientsTotal),
      vmsServiced: safeNum(s.vmsServiced),
      vmsTotal: safeNum(s.vmsTotal),
    },
    rows: routes,
  };
}

export default function useDriverRouteProgress(): UseDriverRouteProgressResult {
  const [snapshot, setSnapshot] = useState<DriverRouteSnapshot>(emptySnapshot());
  const [rows, setRows] = useState<DriverRouteRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const resp = await fetchDriverRouteProgressSnapshot();
      const body = resp.data;

      if (!body?.success) {
        setError(body?.errorMessage || 'Failed to load route driver progress.');
        setSnapshot(emptySnapshot());
        setRows([]);
        return;
      }

      const normalized = normalizePayload(body.data);
      setSnapshot(normalized.snapshot);
      setRows(normalized.rows);
    } catch (e: unknown) {
      setError('Failed to load route driver progress.');
      setSnapshot(emptySnapshot());
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch().catch(() => {
      // refetch already sets error state; swallow to satisfy lint rules
    });
  }, [refetch]);

  return {
    snapshot,
    rows,
    isLoading,
    error,
    refetch,
  };
}
